import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Button,
  Grid,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { useSelector } from '../../../../redux/store';
import { HOST_API_URL } from '../../../../config-global';
import { deleteOrderGuide, downloadOrderGuidePDF, updateOrderGuidePricing } from '../../../../api/order-guides';
import { ScopeGuard } from '../../../../reusable-components/scopes';
import { AwaitButton } from '../../../../reusable-components/await-button';
import OrderGuideVendorPreferences from './OrderGuideVendorPreferences';
import { CustomQuickFilter } from '../../../../reusable-components/datagrid/custom-filters';
import { CustomSettings } from '../../../../reusable-components/datagrid/grid-settings';
import DropdownAction from '../../../../reusable-components/dropdown-action/DropdownAction';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';


const CustomToolbar = (props) => {
  const {
    onSelectClick,
    isSelecting,
    onRemoveSelectedRows,
    active,
    setActive,
    setQuickFilter,
    setFilters,
    setActiveFilter,
    currentOrderGuide,
    columnsState,
    setColumnsState,
    resetGridState,
    handleGridSettingsUpdate,
    gridColumns,
    gridId,
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDownloadExpanded, setIsDownloadExpanded] = useState(false);
  const [openVendorPreferences, setOpenVendorPreferences] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isConfirmRefreshOpen, setIsConfirmRefreshOpen] = useState(false);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsDownloadExpanded(false);
  };
  const handleDownloadToggle = (event) => {
    event.stopPropagation();
    setIsDownloadExpanded((prev) => !prev);
  };
  const handleCloseConfirmDelete = () => {
    setIsConfirmDeleteOpen(false);
  };

  const handleCloseConfirmRefresh = () => {
    setIsConfirmRefreshOpen(false);
  };

  const navigate = useNavigate();
  const { id = '' } = useParams();
  const {
    data: { orderGuide },
  } = useSelector((state) => state.orderGuides);

  const downloadGuide = async () => {
    window.open(`${HOST_API_URL}Api/DownloadOrderGuide?orderGuideActivationId=${id}`, '_blank');
  };

  const downloadGuidePDF = async () => {
    const response = await downloadOrderGuidePDF(id).catch(() =>
      enqueueSnackbar('Order guide PDF failed to download', { variant: 'error' })
    );
    if (response?.status === 200) {
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'OrderGuide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDelete = async () => {
    const response = await deleteOrderGuide(id);
    if (response.status === 200) {
      navigate('/dashboard/admin/order-guides');
    }
  };

  const handleRefresh = async () => {
    await updateOrderGuidePricing(id);
  };

  if (!currentOrderGuide) return null;

  return (
    <>
      <Box>
        <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Grid item sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                ...guideTitleStyles,
                fontWeight: 'bold',
              }}
            >
              Order Guide
            </Typography>
            <Typography
              variant="body1"
              sx={{
                ...guideTitleStyles,
                ml: 1,
                mr: 1,
              }}
            >
              {' '}
              |{' '}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                ...guideTitleStyles,
              }}
            >
              {currentOrderGuide?.facility || currentOrderGuide?.templateName} | {currentOrderGuide?.agoraCategory}
              {/* Order Guide */}
            </Typography>
            <Grid item sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              {!isSelecting ? (
                <Tooltip title="Select items to remove from order guide">
                  <Button variant="outlined" color="secondary" sx={{ maxWidth: 6 }} onClick={onSelectClick}>
                    Select
                  </Button>
                </Tooltip>
              ) : (
                <>
                  <Button variant="outlined" color="secondary" sx={{ mr: 1 }} onClick={onRemoveSelectedRows}>
                    Remove from order guide
                  </Button>
                  <Button color="secondary" sx={{ maxWidth: 6 }} onClick={onSelectClick}>
                    Cancel
                  </Button>
                </>
              )}
            </Grid>
            <IconButton
              aria-label="more"
              aria-controls={isMenuOpen ? 'order-guide-toolbar-menu' : undefined}
              aria-haspopup="true"
              onClick={handleMenuClick}
              sx={{ color: 'secondary.main' }}
            >
              <Iconify icon="mdi:more-vert" />
            </IconButton>
            <Menu
              id="order-guide-toolbar-menu"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    p: 1,
                    minWidth: 200,
                    maxWidth: 280,
                  },
                },
                list: {
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  },
                },
              }}
            >
              {!currentOrderGuide?.locked && (
                <MenuItem
                  disableRipple
                  sx={{
                    pl: 2,
                    pr: 2,
                    height: 40,
                    alignItems: 'center',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'grey.200',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ minWidth: 40, justifyContent: 'flex-start', alignItems: 'center', height: '100%' }}
                  >
                    <Box
                      sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Checkbox
                        checked={active}
                        onChange={(e) => {
                          setActive(e.target.checked);
                          handleMenuClose();
                        }}
                        size="small"
                        sx={{ p: 0 }}
                        inputProps={{ 'aria-label': 'Active' }}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary="Active"
                    primaryTypographyProps={{
                      variant: 'button',
                      sx: { textTransform: 'none', color: 'grey.700', fontSize: '0.875rem', fontWeight: 400 },
                    }}
                  />
                </MenuItem>
              )}

              <MenuItem disableRipple disableGutters sx={flatMenuItemStyles} onClick={handleDownloadToggle}>
                <DropdownAction
                  badgeIcon="material-symbols:download"
                  endIcon={
                    <Iconify
                      icon="mdi:chevron-down"
                      sx={{
                        transform: isDownloadExpanded ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  }
                >
                  Download
                </DropdownAction>
              </MenuItem>

              {isDownloadExpanded && [
                <MenuItem
                  key="download-excel"
                  disableRipple
                  disableGutters
                  sx={flatMenuItemStyles}
                  onClick={() => {
                    downloadGuide();
                    handleMenuClose();
                  }}
                >
                  <DropdownAction>Download as Excel</DropdownAction>
                </MenuItem>,
                <MenuItem
                  key="download-pdf"
                  disableRipple
                  disableGutters
                  sx={flatMenuItemStyles}
                  onClick={() => {
                    downloadGuidePDF();
                    handleMenuClose();
                  }}
                >
                  <DropdownAction>Download as PDF</DropdownAction>
                </MenuItem>,
              ]}

              <ScopeGuard scopes={['Edit-OrderGuide']} allowAdmin>
                {!currentOrderGuide?.locked && (
                  <>
                    <MenuItem
                      disableRipple
                      disableGutters
                      sx={flatMenuItemStyles}
                      onClick={() => {
                        setOpenVendorPreferences(true);
                        handleMenuClose();
                      }}
                    >
                      <DropdownAction badgeIcon="mdi:tune">Vendor Preferences</DropdownAction>
                    </MenuItem>

                    <MenuItem
                      disableRipple
                      disableGutters
                      sx={flatMenuItemStyles}
                      onClick={() => {
                        setIsConfirmRefreshOpen(true);
                        handleMenuClose();
                      }}
                    >
                      <DropdownAction badgeIcon="ic:baseline-loop">Refresh Pricing</DropdownAction>
                    </MenuItem>

                    <Divider sx={{ my: 0.5, mx: 2 }} />

                    <MenuItem
                      disableRipple
                      disableGutters
                      sx={flatMenuItemStyles}
                      onClick={() => {
                        setIsConfirmDeleteOpen(true);
                        handleMenuClose();
                      }}
                    >
                      <DropdownAction
                        badgeIcon="ic:outline-delete"
                        iconColor="error.main"
                        iconBgColor="#FDEDED"
                        textColor="error.main"
                      >
                        Delete Order Guide
                      </DropdownAction>
                    </MenuItem>
                  </>
                )}
              </ScopeGuard>
            </Menu>
          </Grid>
          {/* Right: Main content and search bar */}
          <Grid item xs zeroMinWidth>
            <Grid container direction="row" justifyContent="flex-end" alignItems="center" wrap="nowrap" spacing={1}>
              <Box sx={{ mb: 0, ml: 2, minWidth: 220, maxWidth: 320, flexShrink: 0 }}>
                <CustomQuickFilter
                  sx={{ mt: 0 }}
                  gridId={gridId}
                  setQuickFilter={setQuickFilter}
                  setFilters={setFilters}
                  setActiveFilter={setActiveFilter}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <CustomSettings
                  columnsState={columnsState}
                  setColumnsState={setColumnsState}
                  gridColumns={gridColumns}
                  resetGridState={resetGridState}
                  handleGridSettingsUpdate={handleGridSettingsUpdate}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {!currentOrderGuide?.locked && (
          <OrderGuideVendorPreferences
            orderGuideData={orderGuide}
            orderGuideId={currentOrderGuide?.id}
            handleMenuClose={handleMenuClose}
            openVendorPreferences={openVendorPreferences}
            setOpenVendorPreferences={setOpenVendorPreferences}
          />
        )}
        {isConfirmDeleteOpen && (
          <Dialog
            fullWidth
            maxWidth="xs"
            open={isConfirmDeleteOpen}
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              handleCloseConfirmDelete();
            }}
          >
            <DialogTitle sx={{ pb: 2 }}>Delete Order Guide</DialogTitle>
            <DialogContent sx={{ typography: 'body2' }}>
              {' '}
              Are you sure you want to delete this order guide?{' '}
            </DialogContent>

            <DialogActions>
              <AwaitButton
                variant="contained"
                color="error"
                onClick={() => {
                  handleDelete();
                }}
              >
                Delete
              </AwaitButton>

              <Button variant="outlined" color="inherit" onClick={handleCloseConfirmDelete}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {isConfirmRefreshOpen && (
          <Dialog
            fullWidth
            maxWidth="xs"
            open={isConfirmRefreshOpen}
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              handleCloseConfirmRefresh();
            }}
          >
            <DialogTitle sx={{ pb: 2 }}>Refresh Order Guide</DialogTitle>
            <DialogContent sx={{ typography: 'body2' }}>
              {' '}
              Are you sure you want to fetch prices for this order guide? This may take some time.
            </DialogContent>

            <DialogActions>
              <AwaitButton
                variant="contained"
                color="warning"
                onClick={() => {
                  handleRefresh();
                }}
              >
                Refresh
              </AwaitButton>

              <Button variant="outlined" color="inherit" onClick={handleCloseConfirmRefresh}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </>
  );
};

CustomToolbar.propTypes = {
  gridId: PropTypes.string,
  onSelectClick: PropTypes.func,
  isSelecting: PropTypes.bool,
  onRemoveSelectedRows: PropTypes.func,
  active: PropTypes.bool,
  setActive: PropTypes.func,
  currentOrderGuide: PropTypes.object,
  setQuickFilter: PropTypes.func,
  setFilters: PropTypes.func,
  setActiveFilter: PropTypes.func,
  columnsState: PropTypes.object,
  setColumnsState: PropTypes.func,
  resetGridState: PropTypes.func,
  handleGridSettingsUpdate: PropTypes.func,
  gridColumns: PropTypes.array,
};

export default CustomToolbar;

const guideTitleStyles = {
  color: 'secondary.main',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const flatMenuItemStyles = {
  p: 0,
  minHeight: 'unset',
  '&:hover': {
    bgcolor: 'transparent',
  },
};
